import { FormControl } from '@angular/forms';

import {
  IField,
  ISelect,
  ISelectOption,
} from 'app/services/api5-service/api.interface';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { Option } from '@ispui/select/dist/types';

import { SwitcherControlWithValue } from 'common/dynamic-form/utils/form-preparing';
import { DocHelper } from 'utils/dochelper';

import { SwitcherSelectOption } from './model/switcher-select-option.interface';
import { SwitcherSelectProps } from './model/switcher-select-props.interface';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
  ISPFormState,
} from '../../model';
import {
  getDropdownDirection,
  getDropdownParentSelector,
} from '../select/dropdown.utils';
import {
  isSelectShouldBeHidden,
  isSelectShouldBeHidden$,
} from '../select/select.utils';

const DEFAULT_GROUP_NAME = 'default';

/**
 * Sort options array, move option without group in end array
 *
 * @param options
 */
function sortOptionsWithoutGroupLast(
  options: ISelectOption[],
): ISelectOption[] {
  return [...options].sort((a, b) => {
    if (a.$valuegroup === b.$valuegroup) {
      return 0;
    }

    if (!a.$valuegroup) {
      return 1;
    }

    if (!b.$valuegroup) {
      return -1;
    }
    return 0;
  });
}

/**
 * Get option for switcher select
 *
 * @param groupName
 * @param selectOptions
 * @param index
 * @param control
 * @param field
 * @param state
 */
function getSwitcherSelectOption(
  groupName: string,
  selectOptions: Option[],
  index: number,
  control: ISelect,
  field: IField,
  state: ISPFormState,
): SwitcherSelectOption {
  const doc = state.docSubject.value;
  return {
    text: DocHelper.getMessage(`ostempl_group_${groupName}`, doc),
    img: `${doc.$host}${
      doc.$theme
    }assets/icons/os/${groupName.toLocaleLowerCase()}.svg`,
    select: {
      formControl: new FormControl(selectOptions[0].value, {
        nonNullable: true,
      }),
      anchorType: 'line',
      id: `${groupName}__select__${index}`,
      multiple: false,
      options: selectOptions,
      required: false,
      placeholder: '',
      dropdownPopupClass: 'isp-dynamic-form-select-dropdown-popup',
      notFoundText: DocHelper.getMessage('select_option_not_found', doc),
      selectAllText: DocHelper.getMessage('msg_select_all', doc),
      listWidth: '',
      anchorWidth: '280px',
      anchorMaxWidth: '280px',
      dropdownScrollSelector: state.dropdownScrollSelector,
      dropdownParentSelector: getDropdownParentSelector(state, control, field),
      dropdownDirection: getDropdownDirection(control, field),
      alignment: 'middle',
    },
  };
}

/**
 * Get options array grouped by value group
 *
 * @param options
 */
function getSwitcherGroupedOption(
  options: ISelectOption[],
): Record<string, Option[]> {
  return options.reduce((accumulator, option) => {
    const groupName = option.$valuegroup || DEFAULT_GROUP_NAME;
    if (!accumulator[groupName]) {
      accumulator[groupName] = [];
    }
    accumulator[groupName].push({
      text: option.$,
      value: option.$key,
    });
    return accumulator;
  }, {});
}

/**
 * Map slist options to switcher-select options
 *
 * @param options$
 * @param control
 * @param field
 * @param state
 */
export function getSwitcherSelectOptions$(
  options$: Observable<ISelectOption[]>,
  control: ISelect,
  field: IField,
  state: ISPFormState,
): Observable<SwitcherSelectOption[]> {
  return options$.pipe(
    map(options => {
      const sortedOptions = sortOptionsWithoutGroupLast(options);
      const groupedOptions = getSwitcherGroupedOption(sortedOptions);

      return Object.entries(groupedOptions).map<SwitcherSelectOption>(
        ([groupName, selectOptions], index) => {
          return getSwitcherSelectOption(
            groupName,
            selectOptions,
            index,
            control,
            field,
            state,
          );
        },
      );
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
}

/**
 * Get props for switcher-select field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getSwitcherSelectProps(
  control: ISelect,
  field: IField,
  state: ISPFormState,
): SwitcherSelectProps {
  const options = state.selectService.getOptionsForSelect(control.$name) || [];
  const options$ = state.selectService
    .getOptionsForSelect$(control.$name)
    .pipe(map(optionsSelect => optionsSelect || []));

  return {
    originalControl: control,
    originalField: field,
    options$,
    optionsUi$: getSwitcherSelectOptions$(options$, control, field, state),
    isHidden: isSelectShouldBeHidden(state, control, field, options),
    isHidden$: isSelectShouldBeHidden$(state, control, field, options$),
  };
}
/**
 * Get config for switcher-select field
 *
 * @param control - control (subfield) metadata
 * @param state - dynamic form state
 */
export function getSwitcherSelectConfig(
  control: SwitcherControlWithValue,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.SwitcherSelect> {
  const props = getSwitcherSelectProps(
    control.control,
    control.parent.field,
    state,
  );

  return {
    key: control.control.$name,
    type: ISPFieldType.SwitcherSelect,
    wrappers: [ISPFieldWrapper.FieldBase],
    props,
  };
}
